/**
 * @fileoverview Solar System specs and pricing calculations for solarIQ leads
 * 
 * Calculates system sizing, costs, savings and payback periods.
 * Based on user input and current South African market pricing and tariffs (2026).
 */

///////////////////////////
//current values from 2025 - to be checked and updated
///////////////////////////

// const declarations - sourced from [BLANK]. MUST be edited periodically (updated for 2026).

// Provincial solar rebate rates (% of gross system cost)
const PROVINCE_MAP = { WC: 0.08, GP: 0.06, KZN: 0.05 };

// SA average Eskom tariff (2025): ~R2.50/kWh residential, R2.20 C&I, R1.90 agriculture
const ESKOM_RATE = { 
    residential: 2.50, 
    commercial: 2.20, 
    agriculture: 1.90 
};

// Peak sun hours per province (SA average ~5.5h/day)
const PROVINCE_SUN = { 
    WC: 5.8, GP: 5.5, KZN: 5.2, EC: 5.4, 
    FS: 5.7, LP: 5.9, MP: 5.8, NC: 6.1, NW: 5.6 
};

/**
 * getMarketPricePerKw:
 * gets market price per kW based on system size
 * economies of scale: larger systems cost less per kW
 * @param {number} sysKw - system size in kW
 * @returns {number} price per kW in rands
 */
function getMarketPricePerKw(sysKw) {
  if (sysKw <= 3)  return 22000; // Small systems: ~R22k/kW
  if (sysKw <= 6)  return 20000; // Entry residential: ~R20k/kW
  if (sysKw <= 10) return 18500; // Mid residential: ~R18.5k/kW
  if (sysKw <= 15) return 17000; // Upper residential: ~R17k/kW
  if (sysKw <= 30) return 15500; // Small commercial: ~R15.5k/kW
  if (sysKw <= 50) return 14000; // Mid commercial: ~R14k/kW
  return 12500;                  // Large commercial/industrial: ~R12.5k/kW
}

/**
 * getBatteryEstimate:
 * estimates battery storage requirements and cost 
 * displayed as optional suggestion in box after calcSystem results display
 * general rule of thumb: 1.5x system kW in Kwh storage
 * 
 * @param {number} sysKw - system size in kW
 * @returns {{kwh: number, cost: number}} battery capacity and installed amount
 */
function getBatteryEstimate(sysKw) {
  const batteryKwh = Math.ceil(sysKw * 1.5); // ~1.5x system kW in kWh storage
  return { 
    kwh: batteryKwh, 
    cost: Math.round(batteryKwh * 8500) // ~R8,500/kWh installed
  };
}


/**
 * calcSystem:
 * main system calculation function for lead
 * sizes system, calculates costs, savings and feedback
 * 
 * @param {number} bill - monthly electricity bill (rands)
 * @param {string} [type="residential"] - customer type: "residential", "commercial", "agriculture"
 * @param {string} [province=""] - province code (e.g., "WC", "GP")
 * @param {number|null} [kwhFromBill=null] - actual kWh usage from bill (if extracted)
 * @param {number|null} [rateFromBill=null] - actual tariff rate from bill (if extracted)
 * 
 * FULL LEAD CALCULATION VALUES/INFORMATION
 * 
 * @typedef {object} CalcSystemResult
 * @property {number} bill - original monthly bill (rands)
 * @property {number} sysKw - recommended system size (kW)
 * @property {number} gross - gross system cost before rebates
 * @property {number} sec12b - Section 12B tax incentive amount
 * @property {number} provCr - provincial rebate credit
 * @property {number} net - net cost after all rebates
 * @property {number} payment - estimated monthly finance payment (60 months)
 * @property {number} savings - estimated monthly electricity savings
 * @property {number} netMo - net monthly position (savings - payment)
 * @property {number} payback - payback period in years
 * @property {number} life25 - estimated 25-year net savings
 * @property {number} util25 - estimated 25-year utility spend without solar
 * @property {number} panels - recommended panel count
 * @property {number} pricePerKw - market price per kW used
 * @property {{ kwh: number, cost: number }} battery - battery storage estimate
 * @property {number} monthlyKwh - estimated monthly consumption (kWh)
 * @property {number} monthlyGenKwh - estimated monthly solar generation (kWh)
 * @property {number} coverageRatio - % of usage covered by solar (0-90)
 * @property {number} tariff - tariff rate used in calculation
 * @property {number} sunHours - peak sun hours used
 * @property {number} annualGen - estimated annual generation (kWh)
 * @property {number} co2Saved - estimated annual CO2 saving (kg)
 * @property {"actual_kwh"|"bill_estimate"} sizedFrom - sizing data source
 * 
 *
 * @returns {CalcSystemResult} complete system calculation with all financial metrics
 */

export function calcSystem(bill, type = "residential", province = "", kwhFromBill = null, rateFromBill = null) {
    const isComm = type === "commercial";
    const isAgr = type === "agriculture";
    const saveRate = isComm ? 0.88 : isAgr ? 0.85 : 0.82;
    const prov = province.trim().toUpperCase().slice(0,2);
    const sunHours = PROVINCE_SUN[prov] || 5.5;

    // ── System sizing — use real kWh if extracted from bill, else estimate from bill amount ──
    let monthlyKwh, sysKw, tariff;
    if (kwhFromBill && kwhFromBill > 0) {
        // real kWh from uploaded bill — most accurate
        monthlyKwh = kwhFromBill;
        tariff = rateFromBill && rateFromBill > 0 ? rateFromBill : ESKOM_RATE[type] || 2.50;
        // system size: (monthly kWh / 30 days / sun hours) × 1.25 safety factor
        sysKw = +((monthlyKwh /  30 / sunHours) * 1.25).toFixed(1);
    }

    else {
        // estimate from bill amount: divide by tariff rate to get kWh, then size system
        // use the correct tariff for the type — commercial is R2.20, residential R2.50
        tariff = ESKOM_RATE[type] || 2.50;
        monthlyKwh = Math.round(bill / tariff);
        sysKw = +((monthlyKwh / 30 / sunHours) * 1.25).toFixed(1);
        console.log("ExSolar sizing (bill estimate):", { bill, tariff, monthlyKwh, sysKw, sunHours });
    }

    // enforce reasonable minimums/maximums
    sysKw = Math.max(1.5, Math.min(sysKw, 10000));

    const ppw = getMarketPricePerKw(sysKw);
    const gross = Math.round(sysKw * ppw);

    // TODO: check Section 12B calculation and other values to ensure they are up to date
    // See session handoff for details
    const sec12b = (isComm || isAgr) ? Math.round(gross * 0.27) : 0;
    
    const provCr = Math.round(gross * (PROVINCE_MAP[prov] || 0));
    const net = gross - sec12b - provCr;
    const payment = Math.round(net / 60);

    // monthly generation: sysKw × sunHours × 30 days × 0.8 performance ratio
    const monthlyGenKwh = Math.round(sysKw * sunHours * 30 * 0.8);
    // savings = what % of usage solar covers × bill amount (capped at 90% — grid tie-in)
    const coverageRatio = Math.min(monthlyGenKwh / (monthlyKwh || 1), saveRate);
    const savings = Math.round(bill * coverageRatio);
    const netMo = savings - payment;
    const payback = +(net / (savings * 12)).toFixed(1);
    const annualEskomIncrease = 0.12; // 12% per year average
    const life25 = Math.round(netMo * 12 * 25);
    const util25 = Math.round(bill * ((Math.pow(1 + annualEskomIncrease, 25) - 1) / annualEskomIncrease) * 12);
    const PANEL_W = 0.620; // 620W panels (premium tier, minimum 550W)
    const panels = Math.ceil(sysKw / PANEL_W);
    const pricePerKw = ppw;
    const battery = getBatteryEstimate(sysKw);
    const annualGen = Math.round(sysKw * sunHours * 365 * 0.8);
    const co2Saved = Math.round(annualGen * 0.95); // 0.95 kg CO2 per kWh in SA grid

    return {
        bill, sysKw, gross, sec12b, provCr, net, payment, savings, netMo,
        payback, life25, util25, panels, pricePerKw, battery,
        monthlyKwh, monthlyGenKwh, coverageRatio: Math.round(coverageRatio * 100),
        tariff, sunHours, annualGen, co2Saved,
        sizedFrom: kwhFromBill ? "actual_kwh" : "bill_estimate",
    };
}